import {
  PRODUCT_ARTICLE_IMAGE_HEIGHT,
  PRODUCT_ARTICLE_IMAGE_WIDTH,
} from "./BookListingItem";
import { ProductBundleItem } from "../types";
import { getPageLink } from "@/navigation/selectors";
import { PricelistTag } from "@/products/components/PricelistTag";
import classNames from "classnames";
import Link from "next/link";
import Image from "next/image";
import { Canvas, Typography } from "@cappelendamm/design-system/react";
import { ListingItemProps } from "./types";
import { PricelistPrice } from "@/products/components/PricelistPrice";

export function ProductBundleListingItem({
  item,
  config,
}: ListingItemProps<ProductBundleItem>) {
  const {
    objectID: sku,
    imageUrl,
    price,
    name,
    subjectToVAT,
    priceLists,
    itemDetails: { slug, products },
  } = item;

  const meta = {
    itemType: "Bokpakke",
    itemCount: products ? `${products.length} produkter` : null,
  };
  const href = getPageLink({ pageType: "productBundle", params: slug });
  const urlParams = new URLSearchParams();
  products?.forEach((p) => urlParams.append("sku", p.sku));
  urlParams.set("backgroundColor", "transparent");
  return (
    <article
      className={classNames(
        "row-span-[var(--list-row-count)] grid grid-rows-subgrid gap-1 p-0",
        "active:rounded active:outline-3 active:outline-offset-4 active:outline-[var(--cvr-canvas-background)]",
      )}
    >
      <Link
        href={href}
        aria-labelledby={`item-${sku}`}
        className={classNames(
          "group relative grid grid-rows-subgrid no-underline",
          config?.fields?.series
            ? "row-span-[calc(var(--list-row-count)-1)]"
            : "row-span-full",
        )}
      >
        <Canvas className="order-1 px-2 py-2 md:px-6 md:py-2">
          {products?.length ? (
            <div className="absolute top-0 left-0 flex flex-col gap-2 [&>*:not(:first-child)]:rounded-l-none [&>*:not(:first-child)]:rounded-tr-md">
              <PricelistTag
                count={products.length}
                originalItemPrice={price}
                subjectToVAT={subjectToVAT}
                pricelists={priceLists ?? []}
              />
            </div>
          ) : null}
          <Image
            src={
              imageUrl
                ? imageUrl
                : `/api/images/products?${urlParams.toString()}`
            }
            className={classNames(
              "transition-transform",

              imageUrl || (products && products.length < 3)
                ? "sm:group-hover:scale-102"
                : "duration-500 sm:group-hover:scale-109 sm:group-hover:rotate-3",
            )}
            alt={`Omslag fra pakken "${name}"`}
            width={PRODUCT_ARTICLE_IMAGE_WIDTH * 3}
            height={PRODUCT_ARTICLE_IMAGE_HEIGHT}
            quality={100}
          />
        </Canvas>
        <Typography
          id={`item-${sku}`}
          as="h3"
          variant="title-md"
          className={classNames("order-3 truncate", {
            "sm:group-hover:underline": href,
          })}
        >
          {name}
        </Typography>
        <div className="order-2 text-2xl font-semibold [&_span]:text-left">
          <PricelistPrice
            originalItemPrice={price}
            subjectToVAT={subjectToVAT}
            pricelists={priceLists ?? []}
          />
        </div>
        {config?.fields?.formatMeta ? (
          <div className="order-5 mt-2 flex gap-2 text-sm [&_span]:first-of-type:font-semibold [&_span:not(:first-of-type)]:border-l [&_span:not(:first-of-type)]:pl-2">
            {Object.entries(meta)
              .filter((entry) => Boolean(entry[1]))
              .map(([key, value]) => (
                <span key={key}>{value}</span>
              ))}
          </div>
        ) : null}
      </Link>
    </article>
  );
}
